import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { Upload, CheckCircle2, XCircle, FileText, DollarSign, TrendingDown, Loader2, Truck, AlertTriangle, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (v) => {
  const n = Number(v) || 0;
  return n.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
};

const MOIS_LABELS = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];

export function UberVerificationPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('versements');
  const [versements, setVersements] = useState([]);
  const [commandes, setCommandes] = useState([]);
  const [frais, setFrais] = useState([]);
  const [aggregation, setAggregation] = useState([]);
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [downtime, setDowntime] = useState([]);
  const [insights, setInsights] = useState(null);
  const [accuracy, setAccuracy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [selectedMois, setSelectedMois] = useState(null);
  const [selectedAnnee, setSelectedAnnee] = useState(new Date().getFullYear());
  const [detailCommande, setDetailCommande] = useState(null);
  const [articles, setArticles] = useState([]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = { annee: selectedAnnee };
      if (selectedMois) params.mois = selectedMois;

      const [vRes, cRes, fRes, aRes, sRes, oRes, rRes, dRes, iRes, accRes] = await Promise.all([
        api.get('/uber/versements'),
        api.get('/uber/commandes', { params }),
        api.get('/uber/frais'),
        api.get('/uber/aggregation-jour', { params }),
        api.get('/uber/stats'),
        api.get('/uber/orders'),
        api.get('/uber/ratings'),
        api.get('/uber/menu-downtime'),
        api.get('/uber/insights'),
        api.get('/uber/accuracy'),
      ]);
      setVersements(vRes.data);
      setCommandes(cRes.data);
      setFrais(fRes.data);
      setAggregation(aRes.data);
      setStats(sRes.data);
      setOrders(oRes.data);
      setRatings(rRes.data);
      setDowntime(dRes.data);
      setInsights(iRes.data);
      setAccuracy(accRes.data);
    } catch (err) {
      toast.error('Erreur chargement données Uber');
    } finally {
      setLoading(false);
    }
  }, [api, selectedMois, selectedAnnee]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/uber/import-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const d = res.data;
      toast.success(`Import réussi : ${d.imported} commandes, ${d.frais} frais, ${d.dates_count} jours${d.skipped ? ` (${d.skipped} doublons ignorés)` : ''}`);
      setImportDialogOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'import");
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const handleVerify = async (ref, verifie) => {
    try {
      await api.put(`/uber/versements/${ref}/verify`, { verifie });
      toast.success(verifie ? 'Versement vérifié' : 'Vérification annulée');
      fetchAll();
    } catch {
      toast.error('Erreur');
    }
  };

  const openDetail = async (commande) => {
    setDetailCommande(commande);
    try {
      const res = await api.get('/uber/articles', { params: { workflow_id: commande.workflow_id } });
      setArticles(res.data);
    } catch {
      setArticles([]);
    }
  };

  // Group frais by type
  const fraisParType = frais.reduce((acc, f) => {
    const t = f.description || 'Autre';
    if (!acc[t]) acc[t] = { description: t, total: 0, count: 0, items: [] };
    acc[t].total += f.montant_ttc;
    acc[t].count += 1;
    acc[t].items.push(f);
    return acc;
  }, {});

  // Totals for aggregation
  const aggTotals = aggregation.reduce((acc, j) => ({
    nb: acc.nb + j.nb_commandes,
    brut: acc.brut + j.brut_ttc,
    commission: acc.commission + j.commission_ttc,
    net: acc.net + j.net,
    versement: acc.versement + j.versement,
  }), { nb: 0, brut: 0, commission: 0, net: 0, versement: 0 });

  return (
    <div className="space-y-6" data-testid="uber-verification-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/plateformes')} data-testid="uber-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Vérification Uber Eats</h1>
            <p className="text-muted-foreground text-sm">Réconciliation des paiements et commandes Uber</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(selectedAnnee)} onValueChange={v => setSelectedAnnee(Number(v))}>
            <SelectTrigger className="w-24" data-testid="uber-year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2024,2025,2026,2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={selectedMois ? String(selectedMois) : 'all'} onValueChange={v => setSelectedMois(v === 'all' ? null : Number(v))}>
            <SelectTrigger className="w-32" data-testid="uber-month-select">
              <SelectValue placeholder="Tous" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les mois</SelectItem>
              {MOIS_LABELS.map((m, i) => <SelectItem key={i} value={String(i+1)}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={() => setImportDialogOpen(true)} data-testid="uber-import-btn">
            <Upload className="w-4 h-4 mr-2" /> Import CSV Uber
          </Button>
        </div>
      </div>

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Ventes brut TTC</p>
              <p className="text-xl font-bold text-green-600" data-testid="uber-stat-brut">{fmt(stats.total_brut_ttc)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Commission Uber</p>
              <p className="text-xl font-bold text-red-500" data-testid="uber-stat-commission">{fmt(stats.total_commission_ttc)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Frais divers</p>
              <p className="text-xl font-bold text-orange-500" data-testid="uber-stat-frais">{fmt(stats.total_frais_montant)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs text-muted-foreground">Net versé</p>
              <p className="text-xl font-bold text-blue-600" data-testid="uber-stat-net">{fmt(stats.total_net + stats.total_frais_montant)}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="uber-tabs">
          <TabsTrigger value="versements" data-testid="uber-tab-versements">
            <DollarSign className="w-4 h-4 mr-1" /> Versements ({versements.length})
          </TabsTrigger>
          <TabsTrigger value="commandes" data-testid="uber-tab-commandes">
            <Truck className="w-4 h-4 mr-1" /> Articles ({commandes.length})
          </TabsTrigger>
          <TabsTrigger value="orders" data-testid="uber-tab-orders">
            <FileText className="w-4 h-4 mr-1" /> Commandes ({orders.length})
          </TabsTrigger>
          <TabsTrigger value="ratings" data-testid="uber-tab-ratings">
            <CheckCircle2 className="w-4 h-4 mr-1" /> Notes ({ratings.length})
          </TabsTrigger>
          <TabsTrigger value="downtime" data-testid="uber-tab-downtime">
            <AlertTriangle className="w-4 h-4 mr-1" /> Dispo ({downtime.length})
          </TabsTrigger>
          <TabsTrigger value="accuracy" data-testid="uber-tab-accuracy">
            <CheckCircle2 className="w-4 h-4 mr-1" /> Exactitude ({accuracy.length})
          </TabsTrigger>
          <TabsTrigger value="frais" data-testid="uber-tab-frais">
            <TrendingDown className="w-4 h-4 mr-1" /> Frais ({frais.length})
          </TabsTrigger>
          <TabsTrigger value="aggregation" data-testid="uber-tab-aggregation">
            <FileText className="w-4 h-4 mr-1" /> Par jour ({aggregation.length})
          </TabsTrigger>
        </TabsList>

        {/* VERSEMENTS TAB */}
        <TabsContent value="versements">
          <Card>
            <CardHeader>
              <CardTitle>Réconciliation des versements</CardTitle>
              <CardDescription>Vérifiez chaque versement reçu sur votre compte bancaire</CardDescription>
            </CardHeader>
            <CardContent>
              {versements.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Aucun versement importé</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Référence</TableHead>
                      <TableHead>Date versement</TableHead>
                      <TableHead className="text-right">Commandes</TableHead>
                      <TableHead className="text-right">Brut TTC</TableHead>
                      <TableHead className="text-right">Commission</TableHead>
                      <TableHead className="text-right">Frais</TableHead>
                      <TableHead className="text-right">Net versé</TableHead>
                      <TableHead className="text-center">Statut</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {versements.map(v => (
                      <TableRow key={v.ref} data-testid={`versement-row-${v.ref}`}>
                        <TableCell className="font-mono text-sm">{v.ref}</TableCell>
                        <TableCell>{v.date_versement}</TableCell>
                        <TableCell className="text-right">{v.nb_commandes}</TableCell>
                        <TableCell className="text-right font-medium text-green-600">{fmt(v.total_brut)}</TableCell>
                        <TableCell className="text-right text-red-500">{fmt(v.total_commission)}</TableCell>
                        <TableCell className="text-right text-orange-500">{fmt(v.total_frais)}</TableCell>
                        <TableCell className="text-right font-bold">{fmt(v.total_net)}</TableCell>
                        <TableCell className="text-center">
                          {v.verifie ? (
                            <Badge className="bg-green-100 text-green-700" data-testid={`versement-verified-${v.ref}`}>
                              <CheckCircle2 className="w-3 h-3 mr-1" /> Vérifié
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-amber-600 border-amber-300" data-testid={`versement-pending-${v.ref}`}>
                              <AlertTriangle className="w-3 h-3 mr-1" /> En attente
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant={v.verifie ? "outline" : "default"}
                            onClick={() => handleVerify(v.ref, !v.verifie)}
                            data-testid={`versement-verify-btn-${v.ref}`}
                          >
                            {v.verifie ? 'Annuler' : 'Vérifier'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* COMMANDES TAB */}
        <TabsContent value="commandes">
          <Card>
            <CardHeader>
              <CardTitle>Détail des commandes</CardTitle>
              <CardDescription>{commandes.length} commande(s) importée(s)</CardDescription>
            </CardHeader>
            <CardContent>
              {commandes.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Aucune commande importée</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>ID</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Heure</TableHead>
                        <TableHead>Canal</TableHead>
                        <TableHead>Client</TableHead>
                        <TableHead className="text-right">Brut TTC</TableHead>
                        <TableHead className="text-right">Commission</TableHead>
                        <TableHead className="text-right">Net</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {commandes.map(c => (
                        <TableRow key={c.id} data-testid={`commande-row-${c.order_id}`}>
                          <TableCell className="font-mono text-xs">{c.order_id}</TableCell>
                          <TableCell>{c.date}</TableCell>
                          <TableCell>{c.heure}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">{c.canal || '-'}</Badge>
                          </TableCell>
                          <TableCell className="text-xs">{c.abonnement_client}</TableCell>
                          <TableCell className="text-right font-medium">{fmt(c.ventes_ttc)}</TableCell>
                          <TableCell className="text-right text-red-500">{fmt(c.commission_ttc)}</TableCell>
                          <TableCell className="text-right font-bold">{fmt(c.versement)}</TableCell>
                          <TableCell>
                            <Badge className={c.statut === 'Terminée' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                              {c.statut || '-'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button size="sm" variant="ghost" onClick={() => openDetail(c)} data-testid={`commande-detail-${c.order_id}`}>
                              Détail
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ORDERS TAB - order_history detail */}
        <TabsContent value="orders">
          <Card>
            <CardHeader>
              <CardTitle>Historique des commandes (operationnel)</CardTitle>
              <CardDescription>
                Detail par commande : heures, delais, plateforme, type de traitement.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {insights && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                  <Card className="bg-blue-50/40">
                    <CardContent className="pt-3 pb-3">
                      <p className="text-xs text-muted-foreground">Commandes</p>
                      <p className="text-2xl font-bold">{insights.nb_orders}</p>
                      <p className="text-xs text-green-600">{insights.taux_completion}% completees</p>
                    </CardContent>
                  </Card>
                  <Card><CardContent className="pt-3 pb-3">
                    <p className="text-xs text-muted-foreground">Panier moyen</p>
                    <p className="text-2xl font-bold tabular-nums">{insights.panier_moyen} EUR</p>
                  </CardContent></Card>
                  <Card><CardContent className="pt-3 pb-3">
                    <p className="text-xs text-muted-foreground">Temps prep moyen</p>
                    <p className="text-2xl font-bold tabular-nums">{insights.temps_prep_min} min</p>
                  </CardContent></Card>
                  <Card><CardContent className="pt-3 pb-3">
                    <p className="text-xs text-muted-foreground">Temps livraison</p>
                    <p className="text-2xl font-bold tabular-nums">{insights.temps_livraison_min} min</p>
                  </CardContent></Card>
                  <Card><CardContent className="pt-3 pb-3">
                    <p className="text-xs text-muted-foreground">Note moyenne</p>
                    <p className="text-2xl font-bold tabular-nums">{insights.rating_avg ?? '-'}</p>
                    <p className="text-xs text-muted-foreground">{insights.rating_count} avis</p>
                  </CardContent></Card>
                </div>
              )}
              {orders.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  Aucune commande detaillee. Importe le fichier <b>order_history_local_v2_*.csv</b>.
                </p>
              ) : (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Date / Heure</TableHead>
                    <TableHead>ID</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-center">Articles</TableHead>
                    <TableHead className="text-right">Valeur</TableHead>
                    <TableHead className="text-center">Prep</TableHead>
                    <TableHead className="text-center">Livraison</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {orders.slice(0, 100).map(o => (
                      <TableRow key={o.id} data-testid={`order-${o.id}`}>
                        <TableCell className="text-xs">{o.heure_commande?.replace('T', ' ')}</TableCell>
                        <TableCell className="font-mono text-xs">{o.order_id}</TableCell>
                        <TableCell>
                          <Badge variant={o.statut === 'completed' ? 'default' : 'destructive'}>
                            {o.statut}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs">{o.type_traitement}</TableCell>
                        <TableCell className="text-center">{o.nb_articles}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmt(o.valeur_moyenne)}</TableCell>
                        <TableCell className="text-center text-xs">{o.temps_prep_initial_min} min</TableCell>
                        <TableCell className="text-center text-xs">{o.temps_total_livraison_min} min</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* RATINGS TAB */}
        <TabsContent value="ratings">
          <Card>
            <CardHeader>
              <CardTitle>Notes & Commentaires clients</CardTitle>
              <CardDescription>Notes des clients sur les commandes</CardDescription>
            </CardHeader>
            <CardContent>
              {ratings.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  Aucune note. Importe <b>restaurant_rating_local_*.csv</b>.
                </p>
              ) : (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Commande</TableHead>
                    <TableHead className="text-center">Note</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Etiquettes</TableHead>
                    <TableHead>Commentaire</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {ratings.map(r => (
                      <TableRow key={r.id}>
                        <TableCell className="text-xs">{r.date_note}</TableCell>
                        <TableCell className="font-mono text-xs">{r.order_id}</TableCell>
                        <TableCell className="text-center font-bold">
                          <Badge className={r.valeur >= 4 ? 'bg-green-100 text-green-700' : r.valeur >= 3 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>
                            {r.valeur}/5
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs">{r.rating_type}</TableCell>
                        <TableCell className="text-xs">{r.etiquettes || '-'}</TableCell>
                        <TableCell className="text-xs italic">{r.commentaire || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* DOWNTIME TAB */}
        <TabsContent value="downtime">
          <Card>
            <CardHeader>
              <CardTitle>Disponibilite menu / restaurant</CardTitle>
              <CardDescription>
                Etat de votre boutique heure par heure ({downtime.length} releves)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {insights?.downtime_pct_en_ligne !== null && insights?.downtime_pct_en_ligne !== undefined && (
                <Card className="bg-emerald-50/40 border-emerald-200 mb-4">
                  <CardContent className="pt-3 pb-3 flex items-center gap-3">
                    <CheckCircle2 className="w-8 h-8 text-emerald-600 shrink-0" />
                    <div>
                      <p className="text-2xl font-bold tabular-nums text-emerald-700">
                        {insights.downtime_pct_en_ligne}%
                      </p>
                      <p className="text-xs text-muted-foreground">
                        de creneaux en ligne sur {insights.downtime_days} releves
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}
              {downtime.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  Aucune donnee. Importe <b>restaurant_menu_downtime_local_*.csv</b>.
                </p>
              ) : (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Ouverture</TableHead>
                    <TableHead className="text-center">Menu dispo</TableHead>
                    <TableHead className="text-center">Resto en ligne</TableHead>
                    <TableHead className="text-center">Hors ligne</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {downtime.slice(0, 60).map(d => (
                      <TableRow key={d.id}>
                        <TableCell className="text-xs">{d.date}</TableCell>
                        <TableCell className="text-xs">{d.opening_time?.replace('T', ' ')}</TableCell>
                        <TableCell className="text-center">
                          {d.menu_dispo ? <CheckCircle2 className="w-4 h-4 text-green-600 inline" /> : <XCircle className="w-4 h-4 text-red-500 inline" />}
                        </TableCell>
                        <TableCell className="text-center">
                          {d.resto_en_ligne ? <CheckCircle2 className="w-4 h-4 text-green-600 inline" /> : <XCircle className="w-4 h-4 text-red-500 inline" />}
                        </TableCell>
                        <TableCell className="text-center">
                          {d.resto_hors_ligne ? <Badge variant="destructive">Off</Badge> : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ACCURACY TAB */}
        <TabsContent value="accuracy">
          <Card>
            <CardHeader>
              <CardTitle>Exactitude des commandes</CardTitle>
              <CardDescription>
                Rapport "Order Accuracy Analytics" : taux d'erreur, articles problematiques, remboursements
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {accuracy.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  Aucun rapport. Importe le fichier <b>order_accuracy_analytics_*.xlsx</b>.
                </p>
              ) : accuracy.map(rep => {
                const total = rep.issues?.["Nombre total de commandes terminées"]?.count || 0;
                const issues = ["Articles manquants", "Articles incorrects", "Personnalisations manquantes", "Personnalisations erronées", "Commande erronée livrée"];
                const totalIssues = issues.reduce((s, k) => s + (rep.issues?.[k]?.count || 0), 0);
                const accPct = total > 0 ? ((total - totalIssues) / total * 100).toFixed(1) : 100;
                return (
                  <div key={rep.id} className="space-y-3" data-testid={`accuracy-report-${rep.id}`}>
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold">Période : {rep.periode}</h3>
                      <Badge className="bg-emerald-100 text-emerald-700">
                        {accPct}% exactes
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <Card className="bg-emerald-50/40">
                        <CardContent className="pt-3 pb-3">
                          <p className="text-xs text-muted-foreground">Commandes totales</p>
                          <p className="text-2xl font-bold tabular-nums">{total}</p>
                        </CardContent>
                      </Card>
                      <Card className={totalIssues === 0 ? "bg-emerald-50/40" : "bg-red-50/40"}>
                        <CardContent className="pt-3 pb-3">
                          <p className="text-xs text-muted-foreground">Problèmes détectés</p>
                          <p className={`text-2xl font-bold tabular-nums ${totalIssues === 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {totalIssues}
                          </p>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="pt-3 pb-3">
                          <p className="text-xs text-muted-foreground">Articles problématiques</p>
                          <p className="text-2xl font-bold tabular-nums">{rep.top_articles?.length || 0}</p>
                        </CardContent>
                      </Card>
                    </div>

                    <Table>
                      <TableHeader><TableRow>
                        <TableHead>Type de problème</TableHead>
                        <TableHead className="text-right">Nombre</TableHead>
                        <TableHead className="text-right">Pourcentage</TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {issues.map(k => {
                          const data = rep.issues?.[k] || {};
                          const cnt = data.count || 0;
                          return (
                            <TableRow key={k}>
                              <TableCell>{k}</TableCell>
                              <TableCell className={`text-right font-semibold ${cnt > 0 ? 'text-red-600' : ''}`}>{cnt}</TableCell>
                              <TableCell className="text-right text-muted-foreground">{data.pct || '0%'}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>

                    {rep.top_articles && rep.top_articles.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-2 mt-4">Top articles problématiques</h4>
                        <Table>
                          <TableHeader><TableRow>
                            <TableHead>Article</TableHead>
                            <TableHead>Problème</TableHead>
                            <TableHead className="text-right">Nb</TableHead>
                          </TableRow></TableHeader>
                          <TableBody>
                            {rep.top_articles.map((a, i) => (
                              <TableRow key={i}>
                                <TableCell className="font-medium">{a.article}</TableCell>
                                <TableCell>{a.probleme}</TableCell>
                                <TableCell className="text-right font-bold text-red-600">{a.nb_problemes}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>

        {/* FRAIS TAB */}
        <TabsContent value="frais">
          <Card>
            <CardHeader>
              <CardTitle>Frais & Charges Uber</CardTitle>
              <CardDescription>Dépenses publicitaires, crédits et autres frais</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {Object.keys(fraisParType).length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Aucun frais importé</p>
              ) : (
                Object.values(fraisParType).map(group => (
                  <div key={group.description} data-testid={`frais-group-${group.description}`}>
                    <div className="flex justify-between items-center mb-3">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">{group.description}</h3>
                        <Badge variant="outline">{group.count} entrée(s)</Badge>
                      </div>
                      <span className={`font-bold text-lg ${group.total < 0 ? 'text-red-500' : 'text-green-600'}`}>
                        {fmt(group.total)}
                      </span>
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Réf. versement</TableHead>
                          <TableHead className="text-right">Montant TTC</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {group.items.map(f => (
                          <TableRow key={f.id}>
                            <TableCell>{f.date}</TableCell>
                            <TableCell className="font-mono text-xs">{f.ref_versement}</TableCell>
                            <TableCell className={`text-right font-medium ${f.montant_ttc < 0 ? 'text-red-500' : 'text-green-600'}`}>
                              {fmt(f.montant_ttc)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <Separator className="mt-4" />
                  </div>
                ))
              )}
              {Object.keys(fraisParType).length > 0 && (
                <div className="flex justify-between items-center pt-2 border-t-2">
                  <span className="font-bold text-lg">Total frais</span>
                  <span className={`font-bold text-xl ${frais.reduce((s, f) => s + f.montant_ttc, 0) < 0 ? 'text-red-500' : 'text-green-600'}`}>
                    {fmt(frais.reduce((s, f) => s + f.montant_ttc, 0))}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* AGGREGATION PAR JOUR TAB */}
        <TabsContent value="aggregation">
          <Card>
            <CardHeader>
              <CardTitle>Agrégation par jour</CardTitle>
              <CardDescription>Brut, Commission, TVA et Net par journée</CardDescription>
            </CardHeader>
            <CardContent>
              {aggregation.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Aucune donnée</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Commandes</TableHead>
                      <TableHead className="text-right">Brut TTC</TableHead>
                      <TableHead className="text-right">TVA</TableHead>
                      <TableHead className="text-right">Commission TTC</TableHead>
                      <TableHead className="text-right">Taux</TableHead>
                      <TableHead className="text-right">Versement</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {aggregation.map(j => (
                      <TableRow key={j.date} data-testid={`agg-row-${j.date}`}>
                        <TableCell className="font-medium">{j.date}</TableCell>
                        <TableCell className="text-right">{j.nb_commandes}</TableCell>
                        <TableCell className="text-right font-medium text-green-600">{fmt(j.brut_ttc)}</TableCell>
                        <TableCell className="text-right text-muted-foreground">{fmt(j.tva_ventes)}</TableCell>
                        <TableCell className="text-right text-red-500">{fmt(j.commission_ttc)}</TableCell>
                        <TableCell className="text-right">
                          <Badge variant="outline" className={j.taux_commission > 33 ? 'text-red-600 border-red-300' : ''}>
                            {j.taux_commission}%
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-bold">{fmt(j.versement)}</TableCell>
                      </TableRow>
                    ))}
                    {/* Totals row */}
                    <TableRow className="bg-muted/50 font-bold">
                      <TableCell>TOTAL</TableCell>
                      <TableCell className="text-right">{aggTotals.nb}</TableCell>
                      <TableCell className="text-right text-green-600">{fmt(aggTotals.brut)}</TableCell>
                      <TableCell className="text-right">-</TableCell>
                      <TableCell className="text-right text-red-500">{fmt(aggTotals.commission)}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant="outline">
                          {aggTotals.brut > 0 ? (Math.abs(aggTotals.commission) / aggTotals.brut * 100).toFixed(1) : 0}%
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">{fmt(aggTotals.versement)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent data-testid="uber-import-dialog">
          <DialogHeader>
            <DialogTitle>Import CSV Uber Eats</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Importez n'importe quel rapport Uber Eats : versements, commandes, notes, disponibilité, exactitude...
              Le format est détecté automatiquement et la déduplication est garantie.
            </p>
            <div className="border-2 border-dashed rounded-lg p-6 text-center">
              <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
              <label className="cursor-pointer">
                <span className="text-sm font-medium text-primary hover:underline">
                  {importing ? 'Import en cours...' : 'Choisir un fichier CSV ou XLSX'}
                </span>
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  className="hidden"
                  onChange={handleImport}
                  disabled={importing}
                  data-testid="uber-csv-input"
                />
              </label>
            </div>
            {importing && (
              <div className="flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Traitement...
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!detailCommande} onOpenChange={() => setDetailCommande(null)}>
        <DialogContent className="max-w-lg" data-testid="uber-detail-dialog">
          <DialogHeader>
            <DialogTitle>Commande {detailCommande?.order_id}</DialogTitle>
          </DialogHeader>
          {detailCommande && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground">Date :</span> {detailCommande.date}</div>
                <div><span className="text-muted-foreground">Heure :</span> {detailCommande.heure}</div>
                <div><span className="text-muted-foreground">Canal :</span> {detailCommande.canal}</div>
                <div><span className="text-muted-foreground">Client :</span> {detailCommande.abonnement_client}</div>
              </div>
              <Separator />
              {articles.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Articles commandés</h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Article</TableHead>
                        <TableHead className="text-right">Qté</TableHead>
                        <TableHead className="text-right">PU</TableHead>
                        <TableHead className="text-right">TTC</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {articles.map(a => (
                        <TableRow key={a.id}>
                          <TableCell className="text-sm">{a.nom}</TableCell>
                          <TableCell className="text-right">{a.quantite}</TableCell>
                          <TableCell className="text-right">{fmt(a.prix_unitaire)}</TableCell>
                          <TableCell className="text-right">{fmt(a.ventes_ttc)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              <Separator />
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>Ventes TTC</div><div className="text-right font-medium text-green-600">{fmt(detailCommande.ventes_ttc)}</div>
                <div>Commission Uber</div><div className="text-right text-red-500">{fmt(detailCommande.commission_ttc)}</div>
                <div>Ajustements</div><div className="text-right">{fmt(detailCommande.ajustements)}</div>
                <div className="font-bold">Versement</div><div className="text-right font-bold text-blue-600">{fmt(detailCommande.versement)}</div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
